import DynamicBadge from '@liquidweb-dev/undercurrent/DynamicBadge';
import CalendarClockIcon from '@liquidweb-dev/undercurrent/icons/lucide/CalendarClock';
import ClockAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/ClockAlert';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import DnsAttribute from 'components/standard/DnsAttribute';
import SslAttribute from 'components/standard/SslAttribute';
import {
	type DomainStatus,
	getDomainStatusProps,
	getExpirationStatus,
} from 'modules/domains/statuses';

dayjs.extend(localizedFormat);

export interface AttributesProps {
	domainAutoRenew: boolean;
	domainExpirationDate?: string | null;
	domainStatus: DomainStatus;
	dnsZones: number;
	ssls: number;
	sslWildcard: boolean;
}

const Attributes = ({
	domainAutoRenew,
	domainExpirationDate,
	domainStatus,
	dnsZones,
	ssls,
	sslWildcard,
}: AttributesProps) => {
	const { Icon, color, primaryInfo } = getDomainStatusProps(domainStatus);
	const domainExpirationStatus = getExpirationStatus({
		expiration: domainExpirationDate,
		autorenew: domainAutoRenew,
	});
	const hasExpiringDomain = domainExpirationStatus === 'expiringSoon';

	return (
		<>
			<DynamicBadge
				showShadow={false}
				alwaysExpanded
				primaryInfo={primaryInfo}
				icon={<Icon />}
				color={color}
				sx={{ cursor: 'inherit' }}
			/>

			<DnsAttribute dnsZones={dnsZones} />

			{ssls > 0 && <SslAttribute ssls={ssls} hasWildcard={sslWildcard} />}

			{domainExpirationDate && (
				<Stack
					sx={{
						flexDirection: 'row',
						alignItems: 'center',
						gap: 50,
					}}
				>
					{hasExpiringDomain ? (
						<ClockAlertIcon
							sx={({ size, vars }) => ({
								width: size.xS,
								height: size.xS,
								color: vars.palette.uc.danger.icon.main,
							})}
						/>
					) : (
						<CalendarClockIcon
							sx={({ size, vars }) => ({
								width: size.xS,
								height: size.xS,
								color: vars.palette.uc.icon.weak,
							})}
						/>
					)}
					<Typography
						variant="label-lg"
						sx={({ vars }) => ({
							fontWeight: 500,
							...(hasExpiringDomain && {
								color: vars.palette.uc.danger.text.main,
							}),
						})}
					>
						{dayjs(domainExpirationDate).format('L')}
					</Typography>
				</Stack>
			)}
		</>
	);
};

export default Attributes;
